import React, { useState } from 'react';
import I18n from '@/modules/i18n';
import Axios from 'axios';
import { plato_api_settings_data_path, teacher_program_settings_path } from 'modules/routes';
import Icon from '@/components/ui/Icon';
import Spinner from '@/components/common/Spinner';
import StudentFeatures from '@/components/staffer/Settings/Forms/StudentFeatures';
import StudentAccess from '@/components/staffer/Settings/Forms/StudentAccess';
import GradesSettingsForm from '@/components/staffer/Settings/Forms/GradesSettingsForm';
import useProgramContext from '@/hooks/useProgramContext';
import { TCITooltip } from '@/components/ui/Tooltip';
import Modal, { useModalManager } from '../Modal';
import styles from './index.module.scss';
import { TeacherSettings, StudentFeaturesDataProps, GradesDataProps, StudentAccessDataProps } from './types';

interface SettingsShortcutProps {
  settingsType: 'reading' | 'grades' | 'assignments';
  newIcon?: boolean;
}

type ComponentProps = StudentFeaturesDataProps | GradesDataProps | StudentAccessDataProps;

const SettingsShortcut = ({ settingsType, newIcon = false }: SettingsShortcutProps) => {
  const modalManager = useModalManager();
  const [settings, setSettings] = useState<TeacherSettings | null>(null);
  const [componentProps, setComponentProps] = useState<ComponentProps | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { programId } = useProgramContext();

  const fetchData = async () => {
    if (settings || componentProps) return;

    setIsLoading(true);
    Axios
      .get(plato_api_settings_data_path({ program_id: programId, settings_type: settingsType }))
      .then((res) => {
        const { initialSettings, settingsProps } = res.data.data;
        setSettings(initialSettings);
        setComponentProps(settingsProps);
      })
      .catch(() => ({}))
      .finally(() => setIsLoading(false));
  };

  const updateSettings = ({ settings }) => {
    modalManager.close();
    setSettings(settings);
  };

  const onGradesSubmitSuccess = ({ fieldsChanged, settings }) => {
    if (
      ['notebook_possible_points', 'section_possible_points', 'show_percentages']
        .find(attr => fieldsChanged.includes(attr))
    ) {
      window.location.reload();
    } else updateSettings({ settings });
  };

  const headerText = () => {
    switch (settingsType) {
      case 'assignments': return 'Settings (Assignments)';
      case 'reading': return 'Settings (Reading)';
      case 'grades': return 'Settings (Grades)';
      default: return 'Settings';
    }
  };

  const renderModalBody = () => {
    if (isLoading) return <div className="tw-py-[50px]"><Spinner /></div>;
    if (!settings || !componentProps) return null;

    switch (settingsType) {
      case 'assignments':
        return (
          <StudentAccess
            {...componentProps}
            active
            showLimitedSettings
            handleCancel={modalManager.close}
            settings={settings}
            updateState={updateSettings}
          />
        );
      case 'reading':
        return (
          <StudentFeatures
            {...componentProps}
            active
            showLimitedSettings
            handleCancel={modalManager.close}
            settings={settings}
            updateState={() => window.location.reload()}
          />
        );
      case 'grades':
        return (
          <GradesSettingsForm
            {...componentProps as GradesDataProps}
            handleCancel={modalManager.close}
            settings={settings}
            onSubmitSuccess={onGradesSubmitSuccess}
          />
        );
      default: return null;
    }
  };

  return (
    <>
      <TCITooltip side="bottom">
        <TCITooltip.Trigger asChild>
          <button
            aria-haspopup="dialog"
            aria-label="Open Settings"
            className={`tw-text-tci-purple ${styles.icon}`}
            type="button"
            onClick={async (e) => {
              e.currentTarget.blur();
              await fetchData();
              modalManager.open();
            }}
          >
            {newIcon ? (
              <Icon icon="Settings" />
            ) : (
              <i
                aria-hidden="true"
                className="fa fa-cog non-dropdown tw-text-tci-purple tw-text-[24px]"
              />
            )}
            <span className="sr-only">
              {I18n.t('settings')}
            </span>
          </button>
        </TCITooltip.Trigger>
        <TCITooltip.Content>{I18n.t('settings')}</TCITooltip.Content>
      </TCITooltip>

      <Modal
        aria-labelledby={I18n.t('settings')}
        aria-modal="true"
        bodyClassName="tw-pb-0"
        closeModal={modalManager.close}
        headerText={headerText()}
        id="settingsShortcutModal"
        isOpen={modalManager.isOpen}
        role="dialog"
      >
        <div className={`tw-ml-4 ${styles.settingsShortcutModal}`}>
          <div className="tw-flex tw-items-center tw-justify-end">
            <a href={teacher_program_settings_path(programId)} target="_blank">
              See All Settings <i aria-hidden="true" className="fa fa-external-link" />
            </a>
          </div>

          {renderModalBody()}
        </div>
      </Modal>
    </>
  );
};

export default SettingsShortcut;
